import { useEffect, useRef, useState, useCallback, useLayoutEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useApi } from "@/api/useApi";
import { Save, X, Check, Loader2, Wand2, Frame } from "lucide-react";

const TEAL = "#5FA8B4";
const DEEP_TEAL = "#3A7A8C";
const LIGHT_GREY = "#F0F2F2";

const TEMPLATES = [
  { key: "teal_gradient", label: "Teal Gradient" },
  { key: "clean_white", label: "Clean White" },
  { key: "split", label: "Split Frame" },
  { key: "spotlight", label: "Spotlight" },
];
const ASPECTS = [
  { key: "1:1", label: "Square", w: 1080, h: 1080 },
  { key: "4:5", label: "Portrait", w: 1080, h: 1350 },
  { key: "9:16", label: "Story", w: 1080, h: 1920 },
];

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export default function BrandFrame({ open, onOpenChange, src, filename = "image.png", onSaved, swap }) {
  const api = useApi();
  const canvasRef = useRef(null);
  const [fg, setFg] = useState(null);
  const [logo, setLogo] = useState(null);
  const [teal, setTeal] = useState(TEAL);
  const [deepTeal, setDeepTeal] = useState(DEEP_TEAL);
  const [logoText, setLogoText] = useState("YOUR BRAND");
  const [template, setTemplate] = useState("teal_gradient");
  const [aspect, setAspect] = useState("1:1");
  const [imgScale, setImgScale] = useState(1);
  const [imgPos, setImgPos] = useState({ x: 0, y: 0 });
  const [logoOn, setLogoOn] = useState(true);
  const [text, setText] = useState("");
  const [textColor, setTextColor] = useState("#FFFFFF");
  const [removing, setRemoving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedMedia, setSavedMedia] = useState(null);

  const dims = ASPECTS.find((a) => a.key === aspect);

  useEffect(() => {
    if (!open || !src) return;
    setFg(null); setTemplate("teal_gradient"); setAspect("1:1"); setImgScale(1);
    setImgPos({ x: 0, y: 0 }); setLogoOn(true); setText(""); setTextColor("#FFFFFF"); setSavedMedia(null);
    loadImage(src).then(setFg).catch(() => toast.error("Couldn't load image"));
    Promise.all([api.get("/brand"), api.get("/workspace").catch(() => ({ data: {} }))]).then(([b, w]) => {
      const pal = (b.data?.palette || []).map((p) => p.hex).filter(Boolean);
      if (pal[0]) setTeal(pal[0]);
      if (pal[1]) setDeepTeal(pal[1]);
      setLogoText(b.data?.logo_text || w.data?.name || "YOUR BRAND");
      if (b.data?.logo_url) loadImage(b.data.logo_url).then(setLogo).catch(() => setLogo(null));
      else setLogo(null);
    }).catch(() => { toast.error("Couldn't load brand palette — using defaults"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, src]);

  const drawFg = useCallback((ctx, img, box) => {
    if (!img) return;
    const fit = Math.min(box.w / img.width, box.h / img.height) * imgScale;
    const dw = img.width * fit, dh = img.height * fit;
    const cx = box.x + box.w / 2 + imgPos.x * box.w;
    const cy = box.y + box.h / 2 + imgPos.y * box.h;
    ctx.drawImage(img, cx - dw / 2, cy - dh / 2, dw, dh);
  }, [imgScale, imgPos]);

  const drawLogo = useCallback((ctx, W, H, corner) => {
    if (!logoOn || !logo) return;
    const lw = W * 0.16, lh = (logo.height / logo.width) * lw, m = W * 0.05;
    let x = W - lw - m, y = H - lh - m;
    if (corner === "tl") { x = m; y = m; }
    ctx.drawImage(logo, x, y, lw, lh);
  }, [logo, logoOn]);

  const drawText = useCallback((ctx, str, x, y, maxW, size, color, align = "center") => {
    if (!str) return;
    let fs = size;
    ctx.font = `800 ${fs}px Montserrat, sans-serif`;
    while (ctx.measureText(str).width > maxW && fs > 12) { fs -= 2; ctx.font = `800 ${fs}px Montserrat, sans-serif`; }
    ctx.fillStyle = color; ctx.textAlign = align; ctx.textBaseline = "middle";
    ctx.fillText(str, x, y);
  }, []);

  const render = useCallback((canvas, scale) => {
    if (!canvas || !fg || !dims) return;
    const W = dims.w, H = dims.h;
    canvas.width = Math.round(W * scale);
    canvas.height = Math.round(H * scale);
    const ctx = canvas.getContext("2d");
    ctx.save();
    ctx.scale(scale, scale);
    ctx.clearRect(0, 0, W, H);

    if (template === "teal_gradient") {
      const g = ctx.createLinearGradient(0, 0, W, H);
      g.addColorStop(0, teal); g.addColorStop(1, deepTeal);
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      drawFg(ctx, fg, { x: W * 0.12, y: H * 0.1, w: W * 0.76, h: H * 0.68 });
      drawLogo(ctx, W, H, "br");
      drawText(ctx, text, W / 2, H * 0.9, W * 0.82, W * 0.062, textColor === "#FFFFFF" ? "#FFFFFF" : textColor);
    } else if (template === "clean_white") {
      ctx.fillStyle = "#FFFFFF"; ctx.fillRect(0, 0, W, H);
      ctx.strokeStyle = teal; ctx.lineWidth = W * 0.018;
      const m = W * 0.035; ctx.strokeRect(m, m, W - 2 * m, H - 2 * m);
      drawFg(ctx, fg, { x: W * 0.15, y: H * 0.12, w: W * 0.7, h: H * 0.6 });
      drawLogo(ctx, W, H, "br");
      drawText(ctx, text, W / 2, H * 0.88, W * 0.78, W * 0.055, teal);
    } else if (template === "split") {
      const splitY = H * 0.62;
      ctx.fillStyle = "#FFFFFF"; ctx.fillRect(0, 0, W, splitY);
      drawFg(ctx, fg, { x: W * 0.08, y: H * 0.05, w: W * 0.84, h: splitY - H * 0.1 });
      ctx.fillStyle = teal; ctx.fillRect(0, splitY, W, H - splitY);
      drawText(ctx, logoText, W / 2, splitY + (H - splitY) * 0.36, W * 0.82, W * 0.075, "#FFFFFF");
      drawText(ctx, text || "", W / 2, splitY + (H - splitY) * 0.66, W * 0.82, W * 0.045, "rgba(255,255,255,0.9)");
    } else if (template === "spotlight") {
      ctx.fillStyle = LIGHT_GREY; ctx.fillRect(0, 0, W, H);
      const rg = ctx.createRadialGradient(W / 2, H * 0.45, W * 0.05, W / 2, H * 0.45, W * 0.55);
      rg.addColorStop(0, "rgba(255,255,255,0.95)"); rg.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = rg; ctx.fillRect(0, 0, W, H);
      drawFg(ctx, fg, { x: W * 0.16, y: H * 0.14, w: W * 0.68, h: H * 0.62 });
      drawLogo(ctx, W, H, "tl");
      drawText(ctx, text, W / 2, H * 0.9, W * 0.8, W * 0.055, teal);
    }
    ctx.restore();
  }, [fg, dims, template, teal, deepTeal, logoText, text, textColor, drawFg, drawLogo, drawText]);

  const previewScale = useCallback(() => {
    if (!dims) return 1;
    return Math.min(380 / dims.w, 460 / dims.h, 1);
  }, [dims]);

  useEffect(() => {
    if (!open || !fg) return;
    const raf = requestAnimationFrame(() => render(canvasRef.current, previewScale()));
    return () => cancelAnimationFrame(raf);
  }, [open, fg, render, previewScale]);

  useLayoutEffect(() => {}, []);

  // drag reposition
  const drag = useRef(null);
  const onDown = (e) => {
    drag.current = { sx: e.clientX, sy: e.clientY, pos: { ...imgPos } };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };
  const onMove = (e) => {
    const d = drag.current; if (!d) return;
    const c = canvasRef.current; const rect = c.getBoundingClientRect();
    const nx = d.pos.x + (e.clientX - d.sx) / rect.width;
    const ny = d.pos.y + (e.clientY - d.sy) / rect.height;
    setImgPos({ x: Math.max(-0.5, Math.min(0.5, nx)), y: Math.max(-0.5, Math.min(0.5, ny)) });
  };
  const onUp = () => {
    drag.current = null;
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
  };

  const removeBg = async () => {
    if (!fg) return;
    setRemoving(true);
    try {
      const c = document.createElement("canvas");
      c.width = fg.naturalWidth; c.height = fg.naturalHeight;
      c.getContext("2d").drawImage(fg, 0, 0);
      const res = await api.post("/media/remove-bg", { data_url: c.toDataURL("image/png") });
      const newImg = await loadImage(res.data.data_url);
      setFg(newImg);
      toast.success("Background removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove background.");
    } finally { setRemoving(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const c = document.createElement("canvas");
      render(c, 1);
      const dataUrl = c.toDataURL("image/png");
      const base = (filename || "image").replace(/\.[^.]+$/, "");
      const outName = `${base}-branded-${Date.now()}.png`;
      const res = await api.post("/media/upload-data-url", { data_url: dataUrl, filename: outName, tags: ["branded"] });
      setSavedMedia(res.data);
      toast.success("Branded image saved to Media Library");
      onSaved && onSaved(res.data);
      if (!swap) onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-[#e5e7eb] text-[#3C4048] max-w-4xl w-[95vw] p-0 overflow-hidden" data-testid="brand-frame">
        <DialogTitle className="sr-only">Brand Frame</DialogTitle>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#e5e7eb]">
          <div className="flex items-center gap-2">
            <Frame size={18} className="text-[#5FA8B4]" />
            <h2 className="font-bold text-lg" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Brand Frame</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="frame-save" onClick={save} disabled={saving || !fg} size="sm" className="bg-[#5FA8B4] hover:bg-[#3A7A8C] text-white rounded-[10px] gap-1">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
            </Button>
            <button data-testid="frame-close" onClick={() => onOpenChange(false)} className="text-[#7A9090] hover:text-[#3C4048] p-1"><X size={20} /></button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row">
          <div className="flex-1 bg-[#f5f5f5] flex items-center justify-center p-4 min-h-[280px]">
            <canvas ref={canvasRef} data-testid="frame-canvas" onPointerDown={onDown}
              className="max-w-full h-auto rounded-[8px] shadow-sm cursor-move" style={{ maxHeight: "56vh" }} />
          </div>

          <div className="w-full md:w-[290px] border-t md:border-t-0 md:border-l border-[#e5e7eb] p-4 space-y-4 overflow-y-auto" style={{ maxHeight: "60vh" }}>
            <div>
              <p className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-2">Template</p>
              <div className="grid grid-cols-2 gap-2">
                {TEMPLATES.map((t) => (
                  <button key={t.key} data-testid={`frame-template-${t.key}`} onClick={() => setTemplate(t.key)}
                    className={`py-2.5 rounded-[8px] text-xs font-medium border ${template === t.key ? "border-[#5FA8B4] bg-[#eef7f8] text-[#3A7A8C]" : "border-[#e5e7eb] text-[#3C4048] hover:bg-[#f5f5f5]"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-2">Size</p>
              <div className="flex gap-2">
                {ASPECTS.map((a) => (
                  <button key={a.key} data-testid={`frame-aspect-${a.key}`} onClick={() => setAspect(a.key)}
                    className={`flex-1 py-2 rounded-[8px] text-xs font-medium border ${aspect === a.key ? "border-[#5FA8B4] bg-[#eef7f8] text-[#3A7A8C]" : "border-[#e5e7eb] text-[#3C4048]"}`}>
                    {a.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex justify-between text-xs text-[#7A9090] mb-1"><span>Image scale</span><span>{imgScale.toFixed(1)}x</span></div>
              <input type="range" min={0.3} max={2} step={0.05} value={imgScale} data-testid="frame-scale"
                onChange={(e) => setImgScale(Number(e.target.value))} className="w-full accent-[#5FA8B4]" />
              <p className="text-[10px] text-[#a3b0b0] mt-1">Drag the image on the canvas to reposition.</p>
            </div>

            <label className="flex items-center gap-2 text-sm text-[#3C4048] cursor-pointer">
              <input type="checkbox" checked={logoOn} disabled={!logo} data-testid="frame-logo-toggle" onChange={(e) => setLogoOn(e.target.checked)} className="accent-[#5FA8B4]" />
              Show logo {!logo && <span className="text-[10px] text-[#a3b0b0]">(no logo uploaded)</span>}
            </label>

            <div>
              <p className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5">Text line (optional)</p>
              <Input value={text} maxLength={60} data-testid="frame-text" onChange={(e) => setText(e.target.value)} placeholder="Headline or CTA" className="border-[#e5e7eb] text-[#3C4048] h-9 text-sm" />
              <div className="flex items-center gap-2 mt-2">
                {["#FFFFFF", teal, deepTeal, "#3C4048"].map((c) => (
                  <button key={c} data-testid={`frame-text-color-${c}`} onClick={() => setTextColor(c)} className={`w-6 h-6 rounded-full border ${textColor === c ? "ring-2 ring-[#5FA8B4] ring-offset-1" : "border-[#e5e7eb]"}`} style={{ backgroundColor: c }} />
                ))}
              </div>
            </div>

            <Button data-testid="frame-remove-bg" onClick={removeBg} disabled={removing} variant="outline" className="w-full border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#f0f7f8] rounded-[10px] gap-1">
              {removing ? <><Loader2 size={14} className="animate-spin" /> Removing…</> : <><Wand2 size={14} /> Remove background first</>}
            </Button>

            {swap && savedMedia && (
              <Button data-testid="frame-swap" onClick={() => { swap.handler(savedMedia); onOpenChange(false); }} className="w-full bg-[#3A7A8C] hover:bg-[#2c5f6e] text-white rounded-[10px] gap-1">
                <Check size={14} /> {swap.label || "Use in this post"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
